import { FootballCardAttributes, FootballPosition, CommunityRating } from '../types';

/**
 * Calculates weighted OVR rating for a specific position based on attributes
 */
export function calculatePositionOVR(position: FootballPosition, attrs: FootballCardAttributes): number {
  if (position === 'GK') {
    if (attrs.gkReflexes || attrs.gkDiving) {
      const div = attrs.gkDiving || 70;
      const han = attrs.gkHandling || 70;
      const kic = attrs.gkKicking || 70;
      const ref = attrs.gkReflexes || 75;
      const spd = attrs.gkSpeed || 60;
      const pos = attrs.gkPositioning || 70;
      return Math.round(ref * 0.28 + div * 0.25 + pos * 0.22 + han * 0.15 + kic * 0.05 + spd * 0.05);
    }
    return attrs.goalkeeping || Math.round((attrs.defending + attrs.physical + attrs.pace) / 3);
  }

  const { pace, shooting, passing, dribbling, defending, physical } = attrs;

  switch (position) {
    case 'CB':
    case 'LB':
    case 'RB':
    case 'LWB':
    case 'RWB':
      return Math.round(defending * 0.35 + physical * 0.25 + pace * 0.20 + passing * 0.10 + dribbling * 0.10);

    case 'CDM':
    case 'CM':
      return Math.round(passing * 0.25 + dribbling * 0.20 + defending * 0.20 + physical * 0.20 + pace * 0.10 + shooting * 0.05);

    case 'CAM':
    case 'LM':
    case 'RM':
      return Math.round(passing * 0.30 + dribbling * 0.25 + shooting * 0.20 + pace * 0.15 + physical * 0.10);

    case 'LW':
    case 'RW':
    case 'CF':
    case 'ST':
      return Math.round(shooting * 0.30 + pace * 0.25 + dribbling * 0.25 + passing * 0.10 + physical * 0.10);

    default:
      return Math.round((pace + shooting + passing + dribbling + defending + physical) / 6);
  }
}

/**
 * Calculates ratings across all key positions to show position versatility
 */
export function calculateAllPositionsOVR(attrs: FootballCardAttributes): { position: FootballPosition; ovr: number }[] {
  const positions: FootballPosition[] = ['ST', 'CAM', 'RW', 'CM', 'CDM', 'CB', 'GK'];
  return positions.map(pos => ({
    position: pos,
    ovr: calculatePositionOVR(pos, attrs)
  }));
}

/**
 * Returns preset starting attributes for new players
 */
export function getInitialPresetAttributes(
  preset: 'Beginner' | 'Casual' | 'Average' | 'Good' | 'Very Good' | 'Excellent' | 'Custom'
): FootballCardAttributes {
  let base = 60;
  switch (preset) {
    case 'Beginner': base = 50; break;
    case 'Casual': base = 56; break;
    case 'Average': base = 64; break;
    case 'Good': base = 72; break;
    case 'Very Good': base = 78; break;
    case 'Excellent': base = 85; break;
    default: base = 65; break;
  }

  return {
    overall: base,
    pace: base,
    shooting: Math.max(30, base - 2),
    passing: base,
    dribbling: base,
    defending: Math.max(30, base - 4),
    physical: base,
    goalkeeping: Math.max(30, base - 10),
    gkDiving: Math.max(30, base - 5),
    gkHandling: Math.max(30, base - 5),
    gkKicking: Math.max(30, base - 8),
    gkReflexes: Math.max(30, base - 3),
    gkSpeed: Math.max(30, base - 10),
    gkPositioning: Math.max(30, base - 5),
  };
}

/**
 * Calculates a stat-assisted match performance rating (1.0 to 10.0 scale)
 */
export function calculateStatAssistedMatchRating(stats: {
  goals: number;
  assists: number;
  yellowCards?: number;
  redCards?: number;
  saves?: number;
  isCleanSheet?: boolean;
  isMotm?: boolean;
  isGk?: boolean;
  ownGoals?: number;
}): number {
  let score = 6.0; // standard starting match rating

  score += (stats.goals || 0) * 0.7;
  score += (stats.assists || 0) * 0.4;

  if (stats.isMotm) score += 0.6;

  if (stats.isGk || stats.isCleanSheet) {
    if (stats.isCleanSheet) score += 0.6;
    if (stats.saves) score += Math.min(1.2, stats.saves * 0.15);
  }

  if (stats.yellowCards) score -= stats.yellowCards * 0.4;
  if (stats.redCards) score -= 1.5;
  if (stats.ownGoals) score -= 0.8;

  const clamped = Math.max(1.0, Math.min(10.0, score));
  return Math.round(clamped * 10) / 10;
}

/**
 * Calculates average community rating from peer submissions
 */
export function calculateCommunityAverageOVR(
  communityRatings: CommunityRating[],
  isGk: boolean = false
): FootballCardAttributes | null {
  if (!communityRatings || communityRatings.length === 0) return null;

  const count = communityRatings.length;
  const sum = communityRatings.reduce(
    (acc, cur) => {
      acc.pace += cur.pace || 60;
      acc.shooting += cur.shooting || 60;
      acc.passing += cur.passing || 60;
      acc.dribbling += cur.dribbling || 60;
      acc.defending += cur.defending || 60;
      acc.physical += cur.physical || 60;
      acc.gkDiving += cur.gkDiving || 60;
      acc.gkHandling += cur.gkHandling || 60;
      acc.gkKicking += cur.gkKicking || 60;
      acc.gkReflexes += cur.gkReflexes || 60;
      acc.gkSpeed += cur.gkSpeed || 60;
      acc.gkPositioning += cur.gkPositioning || 60;
      return acc;
    },
    {
      pace: 0, shooting: 0, passing: 0, dribbling: 0, defending: 0, physical: 0,
      gkDiving: 0, gkHandling: 0, gkKicking: 0, gkReflexes: 0, gkSpeed: 0, gkPositioning: 0
    }
  );

  const avgPace = Math.round(sum.pace / count);
  const avgShooting = Math.round(sum.shooting / count);
  const avgPassing = Math.round(sum.passing / count);
  const avgDribbling = Math.round(sum.dribbling / count);
  const avgDefending = Math.round(sum.defending / count);
  const avgPhysical = Math.round(sum.physical / count);

  const avgGkDiving = Math.round(sum.gkDiving / count);
  const avgGkHandling = Math.round(sum.gkHandling / count);
  const avgGkKicking = Math.round(sum.gkKicking / count);
  const avgGkReflexes = Math.round(sum.gkReflexes / count);
  const avgGkSpeed = Math.round(sum.gkSpeed / count);
  const avgGkPositioning = Math.round(sum.gkPositioning / count);

  const attrs: FootballCardAttributes = {
    overall: 60,
    pace: avgPace,
    shooting: avgShooting,
    passing: avgPassing,
    dribbling: avgDribbling,
    defending: avgDefending,
    physical: avgPhysical,
    gkDiving: avgGkDiving,
    gkHandling: avgGkHandling,
    gkKicking: avgGkKicking,
    gkReflexes: avgGkReflexes,
    gkSpeed: avgGkSpeed,
    gkPositioning: avgGkPositioning,
  };

  attrs.overall = isGk
    ? calculatePositionOVR('GK', attrs)
    : calculatePositionOVR('CM', attrs);

  return attrs;
}
